import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import 'keen-slider/keen-slider.min.css';
import { useKeenSlider } from 'keen-slider/react';
import { motion, AnimatePresence } from 'framer-motion';
import RichText from '../../molecules/richtext';
import { media } from '../../../utils/media-queries';

const Facts = ({ slides, title, fact }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [pause, setPause] = useState(false);

  const [sliderRef, slider] = useKeenSlider({
    autoHeight: false,
    initial: 0,
    slideChanged(s) {
      setCurrentSlide(s.details().relativeSlide);
    },
    loop: true,
    slidesPerView: 0,
    spacing: 0,
    /**
     * AUTOPLAY
     */
    duration: 1500,
    dragStart: () => {
      setPause(true);
    },
    dragEnd: () => {
      setPause(false);
    },
  });

  const handlePrevSlide = () => slider.prev();
  const handleNextSlide = () => slider.next();

  /**
   * AUTOPLAY
   */
  const timer = useRef();

  useEffect(() => {
    timer.current = setInterval(() => {
      if (!pause && slider) {
        slider.next();
      }
    }, 3500);
    return () => {
      clearInterval(timer.current);
    };
  }, [pause, slider]);

  return (
    <StyledFacts>
      <p className="bold-small">{title}</p>
      <div className="top">
        <div className="count">{`${currentSlide + 1} of ${slides.length}`}</div>
        <p>{fact[currentSlide].overline}</p>
        <AnimatePresence initial>
          <motion.div
            initial={{ width: '2px' }}
            animate={{ width: '200px' }}
            transition={{ duration: 3.5, loop: Infinity }}
            className="underlined"
          />
        </AnimatePresence>
      </div>
      <div ref={sliderRef} className="keen-slider">
        {slides.map((slide, i) => (
          <div className="keen-slider__slide" key={i}>
            <div className="bold-big">
              <RichText blocks={slide._rawCopy} />
            </div>
          </div>
        ))}

        <div className="controls">
          <button type="button" onClick={handlePrevSlide}>
            ←
          </button>
          <button type="button" onClick={handleNextSlide}>
            →
          </button>
        </div>
      </div>

      <div className="dots">
        {slides.map((slide, i) => (
          // eslint-disable-next-line jsx-a11y/control-has-associated-label
          <button
            type="button"
            onClick={() => slider.moveToSlide(i)}
            className={`dot ${currentSlide === i ? 'active' : ''}`}
            key={i}
          />
        ))}
      </div>
    </StyledFacts>
  );
};

const StyledFacts = styled.div`
  padding: calc(var(--v-spacing-L) * 2) 0;
  margin-bottom: var(--v-spacing-L);
  display: flex;
  flex-direction: column;
  align-items: center;

  .bold-small {
    text-align: center;
    padding-bottom: var(--v-spacing-L);
  }

  .bold-big {
    text-align: center;
    padding-bottom: 0;
    color: var(--red);
  }

  .top {
    display: flex;
    flex-direction: column;
    align-items: center;
    font-size: var(--font-size-0);
    color: var(--blue);
    text-align: center;

    .underlined {
      height: 2px;
      background: var(--blue);
      margin-top: calc(var(--spacing-XS) / 2);
    }
  }

  .keen-slider {
    width: 100%;

    .keen-slider__slide {
      padding: var(--v-spacing-M) 0;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }

    .controls {
      padding: var(--v-spacing-L) 0;
      position: absolute;
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;
      color: var(--blue);

      button {
        height: 2rem;
      }
    }
  }

  .dots {
    width: 100%;
    height: 100%;
    padding-top: var(--v-spacing-L);
    display: flex;
    justify-content: center;

    .dot {
      width: 6px;
      height: 6px;
      background: #d8d8d8;
      border-radius: 50%;
      border: 0;
      margin: 0 10px;
      cursor: pointer;
    }
    .dot.active {
      background: var(--red);
    }
  }

  @media ${media.L} {
    .keen-slider {
      width: 80%;
    }
  }
`;

Facts.propTypes = {
  slides: PropTypes.array.isRequired,
  title: PropTypes.string,
  fact: PropTypes.array,
};

export default Facts;
